import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { 
  Search, 
  Download,
  RefreshCw,
  Gift,
  Package,
  ChevronDown,
  X,
  Check,
  AlertTriangle,
  CreditCard,
  CheckSquare,
  Square,
  Minus,
  Mail,
  MailX,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAdminReturns, getAdminRMAs, bulkStatusUpdate } from '@/lib/api';
import { toast } from 'sonner';
import { format, parseISO, startOfDay, endOfDay, isWithinInterval, subDays, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// Helper: convert UTC ISO string to Italian time
const toItalianTime = (isoString, fmt = 'full') => {
  if (!isoString) return '';
  try {
    const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(isoString);
    const d = new Date(hasTz ? isoString : isoString + 'Z');
    if (isNaN(d.getTime())) return isoString;
    const opts = fmt === 'short'
      ? { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Europe/Rome' }
      : fmt === 'compact'
      ? { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }
      : { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' };
    return d.toLocaleString('it-IT', opts);
  } catch (e) { return isoString; }
};

// --- Multi-select dropdown component ---
const MultiSelectDropdown = ({ label, options, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    const next = selected.includes(val)
      ? selected.filter(s => s !== val)
      : [...selected, val];
    onChange(next);
  };

  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all min-w-[140px]",
          selected.length > 0
            ? "border-primary bg-primary/5 text-primary"
            : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"
        )}
      >
        <span className="truncate">
          {selected.length === 0 ? label : `${label} (${selected.length})`}
        </span>
        {selected.length > 0 ? (
          <X className="w-3.5 h-3.5 ml-auto flex-shrink-0 hover:text-red-500" onClick={clearAll} />
        ) : (
          <ChevronDown className={cn("w-3.5 h-3.5 ml-auto flex-shrink-0 transition-transform", open && "rotate-180")} />
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-64 overflow-y-auto">
          {options.map(opt => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => toggle(opt.value)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors",
                  isSelected ? "bg-primary/5 text-primary font-medium" : "text-gray-700 hover:bg-gray-50"
                )}
                data-testid={`${testId}-option-${opt.value}`}
              >
                <div className={cn(
                  "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                  isSelected ? "bg-primary border-primary" : "border-gray-300"
                )}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                {opt.icon && <opt.icon className="w-3.5 h-3.5 flex-shrink-0" />}
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// New state config with both legacy and new states
const STATUS_CONFIG = {
  // New workflow states
  RICHIESTA: { label: 'Richiesta', color: 'bg-amber-100 text-amber-700' },
  RICHIESTA_DA_VERIFICARE: { label: 'Da Verificare', color: 'bg-orange-100 text-orange-700' },
  IN_ATTESA_DI_ARRIVO: { label: 'In Attesa di Arrivo', color: 'bg-blue-100 text-blue-700' },
  CAMBIO_IN_LAVORAZIONE: { label: 'Cambio in Lavorazione', color: 'bg-purple-100 text-purple-700' },
  COMPLETATO: { label: 'Completato', color: 'bg-emerald-100 text-emerald-700' },
  RESO_RIFIUTATO: { label: 'Reso Rifiutato', color: 'bg-red-100 text-red-700' },
  ARCHIVIATO: { label: 'Archiviato', color: 'bg-gray-100 text-gray-500' },
  // Legacy states
  pending: { label: 'In attesa', color: 'bg-amber-100 text-amber-700' },
  requested: { label: 'Richiesto', color: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approvato', color: 'bg-emerald-100 text-emerald-700' },
  processing: { label: 'In elaborazione', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completato', color: 'bg-gray-100 text-gray-700' },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-700' },
  converted_no_return: { label: 'Convertito', color: 'bg-teal-100 text-teal-700' }
};

const TYPE_CONFIG = {
  exchange: { label: 'Cambio', icon: RefreshCw, color: 'bg-blue-500' },
  voucher: { label: 'Voucher', icon: Gift, color: 'bg-emerald-500' },
  rma: { label: 'Reso', icon: Package, color: 'bg-amber-500' }
};

// "In lavorazione" states (everything except completed/rejected/archived)
const IN_LAVORAZIONE_STATES = [
  'pending', 'requested', 'approved', 'processing',
  'RICHIESTA', 'RICHIESTA_DA_VERIFICARE', 'IN_ATTESA_DI_ARRIVO', 'CAMBIO_IN_LAVORAZIONE'
];

const COMPLETATO_STATES = ['completed', 'COMPLETATO'];
const RIFIUTATO_STATES = ['rejected', 'RESO_RIFIUTATO'];
const ARCHIVIATO_STATES = ['ARCHIVIATO'];

// Available status filters
const STATUS_FILTERS = [
  { id: 'RICHIESTA', label: 'Richiesta' },
  { id: 'RICHIESTA_DA_VERIFICARE', label: 'Da Verificare' },
  { id: 'IN_ATTESA_DI_ARRIVO', label: 'In Attesa di Arrivo' },
  { id: 'CAMBIO_IN_LAVORAZIONE', label: 'Cambio in Lavorazione' },
  { id: 'COMPLETATO', label: 'Completato' },
  { id: 'RESO_RIFIUTATO', label: 'Reso Rifiutato' },
  { id: 'ARCHIVIATO', label: 'Archiviato' },
  // Legacy
  { id: 'pending', label: 'In attesa (legacy)' },
  { id: 'approved', label: 'Approvato (legacy)' },
  { id: 'processing', label: 'In elaborazione (legacy)' },
];

const AdminResiPage = () => {
  const navigate = useNavigate();
  const context = useOutletContext();
  const userRole = context?.userRole || localStorage.getItem('admin_role') || 'admin';
  const sidebarOpen = context?.isSidebarOpen ?? true;
  const isMobile = context?.isMobile ?? false;
  
  const [returns, setReturns] = useState([]);
  const [rmas, setRMAs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [mainFilter, setMainFilter] = useState('in_lavorazione');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTargetStatus, setBulkTargetStatus] = useState('');
  const [bulkSendEmails, setBulkSendEmails] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Scroll position persistence & scroll-to-top
  const [showScrollTop, setShowScrollTop] = useState(false);
  const SCROLL_KEY = 'resi_scroll_pos';
  const FILTER_KEY = 'resi_main_filter';

  useEffect(() => {
    // Restore saved filter tab
    const savedFilter = sessionStorage.getItem(FILTER_KEY);
    if (savedFilter) {
      setMainFilter(savedFilter);
    }
  }, []);

  useEffect(() => {
    // Restore scroll position ONLY after data has loaded
    if (isLoading) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved) {
      const pos = parseInt(saved, 10);
      sessionStorage.removeItem(SCROLL_KEY);
      sessionStorage.removeItem(FILTER_KEY);
      // Wait for DOM to fully render with data
      const timer = setTimeout(() => window.scrollTo(0, pos), 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [returnsData, rmasData] = await Promise.all([
        getAdminReturns(),
        getAdminRMAs()
      ]);
      setReturns(returnsData || []);
      setRMAs(rmasData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setIsLoading(false);
  };

  // Combine and process all requests
  const allRequests = useMemo(() => {
    const combined = [
      ...returns.map(r => ({
        ...r,
        type: r.request_type,
        date: r.created_at,
        totalValue: r.return_items?.reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0) || 0,
        itemCount: r.return_items?.length || 0,
        customer: r.customer_name
      })),
      ...rmas.map(r => ({
        ...r,
        id: r.rma_id,
        type: 'rma',
        date: r.created_at,
        totalValue: r.items?.reduce((sum, item) => sum + parseFloat(item.price), 0) || 0,
        itemCount: r.items?.length || 0,
        customer: r.customer_name
      }))
    ];
    return combined.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [returns, rmas]);

  // Apply filters
  const filteredRequests = useMemo(() => {
    let filtered = [...allRequests];

    // For logistics role: show IN_ATTESA_DI_ARRIVO + store delivery requests in active statuses
    if (userRole === 'logistics') {
      filtered = filtered.filter(r => 
        r.status === 'IN_ATTESA_DI_ARRIVO' ||
        (r.delivery_method === 'store' && !['COMPLETATO', 'completed', 'ARCHIVIATO', 'RESO_RIFIUTATO', 'rejected'].includes(r.status))
      );
      // Search filter only
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(r => 
          r.order_number?.toLowerCase().includes(query) ||
          r.customer?.toLowerCase().includes(query) ||
          r.customer_email?.toLowerCase().includes(query)
        );
      }
      return filtered;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.order_number?.toLowerCase().includes(query) ||
        r.customer?.toLowerCase().includes(query) ||
        r.customer_email?.toLowerCase().includes(query)
      );
    }

    // Main tab filter
    if (mainFilter === 'in_lavorazione') {
      filtered = filtered.filter(r => IN_LAVORAZIONE_STATES.includes(r.status));
    } else if (mainFilter === 'completato') {
      filtered = filtered.filter(r => COMPLETATO_STATES.includes(r.status));
    } else if (mainFilter === 'archiviato') {
      filtered = filtered.filter(r => ARCHIVIATO_STATES.includes(r.status));
    }
    // 'tutti' = no filter

    // Type filter (multi-select AND)
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(r => selectedTypes.includes(r.type));
    }

    // Status filter (multi-select AND)
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(r => selectedStatuses.includes(r.status));
    }

    // Date filter
    if (dateFilter === 'today') {
      const todayStart = startOfDay(new Date());
      const todayEnd = endOfDay(new Date());
      filtered = filtered.filter(r => {
        const d = new Date(r.date);
        return isWithinInterval(d, { start: todayStart, end: todayEnd });
      });
    } else if (dateFilter === 'week') {
      const weekStart = startOfDay(subDays(new Date(), 7));
      const weekEnd = endOfDay(new Date());
      filtered = filtered.filter(r => {
        const d = new Date(r.date);
        return isWithinInterval(d, { start: weekStart, end: weekEnd });
      });
    } else if (dateFilter === 'month') {
      const monthStart = startOfDay(subMonths(new Date(), 1));
      const monthEnd = endOfDay(new Date());
      filtered = filtered.filter(r => {
        const d = new Date(r.date);
        return isWithinInterval(d, { start: monthStart, end: monthEnd });
      });
    } else if (dateFilter === 'custom' && customDateFrom && customDateTo) {
      const from = startOfDay(new Date(customDateFrom));
      const to = endOfDay(new Date(customDateTo));
      filtered = filtered.filter(r => {
        const d = new Date(r.date);
        return isWithinInterval(d, { start: from, end: to });
      });
    }

    return filtered;
  }, [allRequests, searchQuery, mainFilter, selectedTypes, selectedStatuses, dateFilter, customDateFrom, customDateTo, userRole]);

  // Stats for tabs
  const stats = useMemo(() => ({
    all: allRequests.length,
    in_lavorazione: allRequests.filter(r => IN_LAVORAZIONE_STATES.includes(r.status)).length,
    completato: allRequests.filter(r => COMPLETATO_STATES.includes(r.status)).length,
    archiviato: allRequests.filter(r => ARCHIVIATO_STATES.includes(r.status)).length
  }), [allRequests]);

  const handleViewDetail = (request) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    sessionStorage.setItem(FILTER_KEY, mainFilter);
    navigate(`/admindea26/resi/${request.id}`);
  };

  // Dropdown options
  const typeOptions = [
    { value: 'exchange', label: 'Cambio', icon: RefreshCw },
    { value: 'voucher', label: 'Voucher', icon: Gift },
    { value: 'rma', label: 'Reso', icon: Package },
  ];

  const statusOptions = STATUS_FILTERS.map(sf => ({
    value: sf.id,
    label: sf.label,
  }));

  const exportCSV = () => {
    const headers = ['Data', 'Ordine', 'Cliente', 'Email', 'Tipo', 'Articoli', 'Valore', 'Stato'];
    const rows = filteredRequests.map(r => [
      toItalianTime(r.date, 'short'),
      r.order_number,
      r.customer,
      r.customer_email,
      TYPE_CONFIG[r.type]?.label || r.type,
      r.itemCount,
      `€${r.totalValue.toFixed(2)}`,
      STATUS_CONFIG[r.status]?.label || r.status
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `resi_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
  };

  // --- Bulk selection helpers ---
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRequests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRequests.map(r => r.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkTargetStatus('');
    setBulkSendEmails(false);
  };

  const handleBulkConfirm = async () => {
    if (!bulkTargetStatus || selectedIds.size === 0) return;
    setIsBulkProcessing(true);
    try {
      const result = await bulkStatusUpdate(
        Array.from(selectedIds),
        bulkTargetStatus,
        bulkSendEmails
      );
      const msg = `${result.updated} richieste aggiornate` +
        (result.emails_sent > 0 ? `, ${result.emails_sent} email inviate` : '') +
        (result.errors?.length > 0 ? `. ${result.errors.length} errori.` : '');
      toast.success(msg);
      clearSelection();
      setShowBulkConfirm(false);
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento bulk');
    }
    setIsBulkProcessing(false);
  };

  const BULK_STATUS_OPTIONS = [
    { value: 'RICHIESTA', label: 'Richiesta' },
    { value: 'RICHIESTA_DA_VERIFICARE', label: 'Da Verificare' },
    { value: 'IN_ATTESA_DI_ARRIVO', label: 'In Attesa di Arrivo' },
    { value: 'CAMBIO_IN_LAVORAZIONE', label: 'In Lavorazione' },
    { value: 'COMPLETATO', label: 'Completato' },
    { value: 'RESO_RIFIUTATO', label: 'Rifiutato' },
    { value: 'ARCHIVIATO', label: 'Archiviato' },
  ];

  const isAllSelected = filteredRequests.length > 0 && selectedIds.size === filteredRequests.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < filteredRequests.length;

  // Get row background class based on status
  const getRowBgClass = (status) => {
    if (COMPLETATO_STATES.includes(status)) return 'bg-emerald-50/30';
    if (RIFIUTATO_STATES.includes(status)) return 'bg-red-50/30';
    if (ARCHIVIATO_STATES.includes(status)) return 'bg-gray-50/50';
    return '';
  };

  // Get inline note for row
  const getInlineNote = (request) => {
    if (COMPLETATO_STATES.includes(request.status)) {
      const completedDate = request.completed_at 
        ? toItalianTime(request.completed_at, 'short')
        : toItalianTime(request.updated_at, 'short');
      
      if (request.type === 'exchange') {
        if (request.delivery_method === 'store') {
          return `Il Cambio è stato consegnato in negozio in data ${completedDate}`;
        }
        return `Il Cambio è stato spedito in data ${completedDate}`;
      } else if (request.type === 'voucher') {
        if (request.delivery_method === 'store') {
          return `Il Voucher è stato consegnato in negozio in data ${completedDate}`;
        }
        return `Il Voucher è stato emesso in data ${completedDate}`;
      } else if (request.type === 'rma') {
        return `Il rimborso è stato emesso in data ${completedDate}`;
      }
    }
    if (RIFIUTATO_STATES.includes(request.status)) {
      const reason = request.rejection_reason || request.admin_notes || 'Motivazione non specificata';
      return `Il rimborso non può essere emesso in quanto ${reason}`;
    }
    if (ARCHIVIATO_STATES.includes(request.status)) {
      const reason = request.archived_reason || 'Nessun motivo specificato';
      return `Archiviato: ${reason}`;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 min-w-0 overflow-x-hidden" data-testid="admin-resi-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl lg:text-2xl font-heading font-semibold text-gray-900">
            {userRole === 'logistics' ? 'Resi - Pacchi e Consegne Negozio' : 'Resi'}
          </h1>
          <p className="text-gray-500 text-sm">
            {userRole === 'logistics' 
              ? 'Pacchi in arrivo e richieste con consegna in negozio' 
              : 'Visualizza e gestisci tutte le richieste'
            }
          </p>
        </div>
        
        {userRole === 'admin' && (
          <Button onClick={exportCSV} variant="outline" className="flex items-center gap-2 self-start sm:self-auto" data-testid="export-csv-btn">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Esporta CSV</span>
            <span className="sm:hidden">CSV</span>
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Cerca per ordine, nome cliente o email..."
          className="pl-10 bg-white border-gray-200"
          data-testid="search-input"
        />
      </div>

      {/* Main Tabs - Only for admin */}
      {userRole === 'admin' && (
        <div className="flex items-center gap-2 mb-4 border-b border-gray-200 pb-4 overflow-x-auto -mx-4 px-4 scrollbar-hide">
          {[
            { id: 'in_lavorazione', label: 'In Lavorazione', count: stats.in_lavorazione },
            { id: 'completato', label: 'Completato', count: stats.completato },
            { id: 'archiviato', label: 'Archiviato', count: stats.archiviato },
            { id: 'tutti', label: 'Tutti', count: stats.all }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setMainFilter(tab.id)}
              className={cn(
                "px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 whitespace-nowrap flex-shrink-0",
                mainFilter === tab.id 
                  ? "bg-primary text-white" 
                  : "text-gray-600 hover:bg-gray-100"
              )}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              <span className={cn(
                "px-2 py-0.5 text-xs rounded-full",
                mainFilter === tab.id 
                  ? "bg-white/20 text-white" 
                  : "bg-gray-100 text-gray-600"
              )}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Filters Row - Only for admin */}
      {userRole === 'admin' && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <MultiSelectDropdown
              label="Tipo"
              options={typeOptions}
              selected={selectedTypes}
              onChange={setSelectedTypes}
              testId="filter-type"
            />
            <MultiSelectDropdown
              label="Stato"
              options={statusOptions}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
              testId="filter-status"
            />
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-1.5 sm:ml-auto overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
            {[
              { id: 'all', label: 'Tutte' },
              { id: 'today', label: 'Oggi' },
              { id: 'week', label: 'Settimana' },
              { id: 'month', label: 'Mese' },
              { id: 'custom', label: 'Personalizzate' }
            ].map(df => (
              <button
                key={df.id}
                onClick={() => setDateFilter(df.id)}
                className={cn(
                  "px-3 py-2 rounded-lg text-sm font-medium transition-all border",
                  dateFilter === df.id
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"
                )}
                data-testid={`filter-date-${df.id}`}
              >
                {df.label}
              </button>
            ))}
            
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-2 ml-1">
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="date-from"
                />
                <span className="text-gray-400 text-sm">—</span>
                <input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="date-to"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Action Bar — Sticky bottom, respects sidebar */}
      {selectedIds.size > 0 && userRole === 'admin' && (
        <div
          className={cn(
            "fixed bottom-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3",
            isMobile ? "left-0" : sidebarOpen ? "left-64" : "left-20"
          )}
          style={{ transition: 'left 300ms' }}
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-shrink-0">
              <CheckSquare className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">{selectedIds.size} sel.</span>
              <button onClick={clearSelection} className="text-xs text-gray-400 hover:text-red-500 underline">Annulla</button>
            </div>
            <Select value={bulkTargetStatus} onValueChange={setBulkTargetStatus}>
              <SelectTrigger className="w-44 sm:w-52 bg-white border-gray-300 h-9 text-sm" data-testid="bulk-status-select">
                <SelectValue placeholder="Nuovo stato..." />
              </SelectTrigger>
              <SelectContent>
                {BULK_STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => setShowBulkConfirm(true)}
              disabled={!bulkTargetStatus}
              size="sm"
              className="flex items-center gap-1.5"
              data-testid="bulk-apply-btn"
            >
              <Check className="w-3.5 h-3.5" />
              Applica
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Confirm Dialog */}
      {showBulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="bulk-confirm-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Conferma azione bulk</h3>
            <p className="text-sm text-gray-600 mb-4">
              Stai per cambiare <strong>{selectedIds.size} richieste</strong> in stato{' '}
              <span className="font-semibold text-primary">{BULK_STATUS_OPTIONS.find(o => o.value === bulkTargetStatus)?.label}</span>.
            </p>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4" data-testid="bulk-email-toggle">
              <div className="flex items-center gap-2">
                {bulkSendEmails ? <Mail className="w-4 h-4 text-emerald-600" /> : <MailX className="w-4 h-4 text-gray-400" />}
                <span className="text-sm text-gray-700">Invia email ai clienti</span>
              </div>
              <Switch
                checked={bulkSendEmails}
                onCheckedChange={setBulkSendEmails}
                data-testid="bulk-email-switch"
              />
            </div>

            {bulkSendEmails && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                <p className="text-xs text-amber-700">Le email associate allo stato selezionato verranno inviate a ciascun cliente.</p>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setShowBulkConfirm(false)} disabled={isBulkProcessing} data-testid="bulk-cancel-btn">
                Annulla
              </Button>
              <Button onClick={handleBulkConfirm} disabled={isBulkProcessing} data-testid="bulk-confirm-btn">
                {isBulkProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {isBulkProcessing ? 'Aggiornamento...' : `Conferma (${selectedIds.size})`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {userRole === 'admin' && (
                <th className="w-12 px-3 py-4">
                  <button onClick={toggleSelectAll} className="flex items-center justify-center" data-testid="select-all-checkbox">
                    {isAllSelected ? (
                      <CheckSquare className="w-5 h-5 text-primary" />
                    ) : isSomeSelected ? (
                      <Minus className="w-5 h-5 text-primary p-0.5 border-2 border-primary rounded" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-300" />
                    )}
                  </button>
                </th>
              )}
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ordine</th>
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo</th>
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Articoli</th>
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Valore</th>
              <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredRequests.map((request) => {
              const typeConfig = TYPE_CONFIG[request.type];
              const statusConfig = STATUS_CONFIG[request.status] || { label: request.status, color: 'bg-gray-100 text-gray-700' };
              const Icon = typeConfig?.icon || Package;
              const rowBg = getRowBgClass(request.status);
              const inlineNote = getInlineNote(request);
              const hasLogisticsAlert = request.logistics_alert?.active;
              
              // Pallino "pacco arrivato" - visibile finché admin non completa l'azione
              const DONE_STATES = ['COMPLETATO', 'completed', 'ARCHIVIATO', 'RESO_RIFIUTATO', 'rejected'];
              const hasArrivalDot = request.arrival_confirmed_at && !DONE_STATES.includes(request.status);

              return (
                <React.Fragment key={request.id}>
                  <tr
                    className={cn(
                      "hover:bg-gray-50/50 cursor-pointer group transition-opacity duration-300",
                      hasLogisticsAlert ? "bg-red-50 border-l-4 border-l-red-500" : rowBg,
                      selectedIds.has(request.id) && "bg-primary/5"
                    )}
                    onClick={() => handleViewDetail(request)}
                    data-testid={`request-row-${request.id}`}
                  >
                    {userRole === 'admin' && (
                      <td className="w-12 px-3 py-4">
                        <button
                          onClick={(e) => toggleSelect(request.id, e)}
                          className="flex items-center justify-center"
                          data-testid={`select-${request.id}`}
                        >
                          {selectedIds.has(request.id) ? (
                            <CheckSquare className="w-5 h-5 text-primary" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-300 group-hover:text-gray-400" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {hasArrivalDot && (
                          <span className="relative flex h-3 w-3 flex-shrink-0" data-testid={`arrival-dot-${request.id}`} title="Pacco arrivato in logistica">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                          </span>
                        )}
                        <div>
                          <span className="text-sm text-gray-900">
                            {toItalianTime(request.date, 'short')}
                          </span>
                          <p className="text-xs text-gray-400">
                            {toItalianTime(request.date, 'compact').split(', ').pop()}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        #{request.order_number}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{request.customer}</span>
                      <p className="text-xs text-gray-400">{request.customer_email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white",
                            typeConfig?.color || 'bg-gray-500'
                          )}>
                            <Icon className="w-3 h-3" />
                            {typeConfig?.label || request.type}
                          </span>
                          {request.is_test && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid={`test-badge-${request.id}`}>
                              TEST
                            </span>
                          )}
                        </div>
                        {request.paid_with_gift_card && (
                          <span className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white bg-violet-500 w-fit"
                          )} data-testid={`gift-card-badge-${request.id}`}>
                            <CreditCard className="w-3 h-3" />
                            GIFT CARD
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{request.itemCount}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        €{request.totalValue.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "inline-flex px-2.5 py-1 rounded-full text-xs font-medium",
                          statusConfig.color
                        )}>
                          {statusConfig.label}
                        </span>
                        {hasLogisticsAlert && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white animate-pulse" data-testid={`logistics-alert-badge-${request.id}`}>
                            <AlertTriangle className="w-3 h-3" />
                            RIFIUTA
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {hasLogisticsAlert && (
                    <tr className="bg-red-50 cursor-pointer" onClick={() => handleViewDetail(request)}>
                      <td colSpan={userRole === 'admin' ? 8 : 7} className="px-6 pb-2 pt-0 text-center">
                        <p className="text-xs font-semibold text-red-700 flex items-center justify-center gap-1.5" data-testid={`logistics-alert-msg-${request.id}`}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {request.logistics_alert.message}
                        </p>
                      </td>
                    </tr>
                  )}
                  {inlineNote && !hasLogisticsAlert && (
                    <tr className={cn("cursor-pointer", rowBg)} onClick={() => handleViewDetail(request)}>
                      <td colSpan={userRole === 'admin' ? 8 : 7} className="px-6 pb-3 pt-0 text-center">
                        <p className={cn("text-xs italic", COMPLETATO_STATES.includes(request.status) ? "text-emerald-600" : "text-red-600")}>
                          {inlineNote}
                        </p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessuna richiesta trovata</p>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {filteredRequests.map((request) => {
          const typeConfig = TYPE_CONFIG[request.type];
          const statusConfig = STATUS_CONFIG[request.status] || { label: request.status, color: 'bg-gray-100 text-gray-700' };
          const Icon = typeConfig?.icon || Package;
          const hasLogisticsAlert = request.logistics_alert?.active;
          const inlineNote = getInlineNote(request);
          const DONE_STATES_M = ['COMPLETATO', 'completed', 'ARCHIVIATO', 'RESO_RIFIUTATO', 'rejected'];
          const hasArrivalDot = request.arrival_confirmed_at && !DONE_STATES_M.includes(request.status);
          
          return (
            <div
              key={request.id}
              className={cn(
                "bg-white rounded-xl border border-gray-200 p-4 cursor-pointer active:bg-gray-50 transition-colors",
                hasLogisticsAlert && "border-red-300 bg-red-50",
                selectedIds.has(request.id) && "border-primary bg-primary/5"
              )}
              onClick={() => handleViewDetail(request)}
              data-testid={`request-card-${request.id}`}
            >
              {/* Top row: checkbox + order + date */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {userRole === 'admin' && (
                    <button onClick={(e) => toggleSelect(request.id, e)} className="flex-shrink-0" data-testid={`select-card-${request.id}`}>
                      {selectedIds.has(request.id) ? (
                        <CheckSquare className="w-5 h-5 text-primary" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-300" />
                      )}
                    </button>
                  )}
                  {hasArrivalDot && (
                    <span className="relative flex h-3 w-3 flex-shrink-0" title="Pacco arrivato in logistica">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                  )}
                  <span className="text-sm font-semibold text-gray-900">#{request.order_number}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {toItalianTime(request.date, 'compact')}
                </span>
              </div>
              
              {/* Customer */}
              <p className="text-sm text-gray-700 mb-2">{request.customer}</p>
              <p className="text-xs text-gray-400 mb-3">{request.customer_email}</p>
              
              {/* Badges row: type + value + status */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-col gap-1">
                  <span className={cn(
                    "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white w-fit",
                    typeConfig?.color || 'bg-gray-500'
                  )}>
                    <Icon className="w-3 h-3" />
                    {typeConfig?.label || request.type}
                  </span>
                  {request.paid_with_gift_card && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white bg-violet-500 w-fit">
                      <CreditCard className="w-3 h-3" />
                      GIFT CARD
                    </span>
                  )}
                </div>
                {request.is_test && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-300">
                    TEST
                  </span>
                )}
                <span className="text-xs font-medium text-gray-700">
                  {request.itemCount} art. · €{request.totalValue.toFixed(2)}
                </span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className={cn(
                    "inline-flex px-2 py-0.5 rounded-full text-xs font-medium",
                    statusConfig.color
                  )}>
                    {statusConfig.label}
                  </span>
                </div>
              </div>

              {/* Logistics alert */}
              {hasLogisticsAlert && (
                <div className="mt-3 bg-red-600 rounded-lg px-3 py-2 flex items-center gap-2" data-testid={`logistics-alert-card-${request.id}`}>
                  <AlertTriangle className="w-3.5 h-3.5 text-white flex-shrink-0" />
                  <p className="text-xs font-semibold text-white leading-tight">{request.logistics_alert.message}</p>
                </div>
              )}
              
              {/* Inline note */}
              {inlineNote && !hasLogisticsAlert && (
                <p className={cn(
                  "text-xs italic mt-2 text-center",
                  COMPLETATO_STATES.includes(request.status) ? "text-emerald-600" : "text-red-600"
                )}>
                  {inlineNote}
                </p>
              )}
            </div>
          );
        })}

        {filteredRequests.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessuna richiesta trovata</p>
          </div>
        )}
      </div>

      {filteredRequests.length > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
          <span>Mostrati {filteredRequests.length} risultati</span>
        </div>
      )}

      {/* Bottom spacer when bulk bar is visible */}
      {selectedIds.size > 0 && <div className="h-20" />}

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed z-30 w-10 h-10 bg-gray-900 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-700 transition-all hover:scale-110 right-5"
          style={{ bottom: selectedIds.size > 0 ? '4.5rem' : '1.5rem' }}
          data-testid="scroll-to-top-btn"
          title="Torna su"
        >
          <ChevronDown className="w-5 h-5 rotate-180" />
        </button>
      )}
    </div>
  );
};

export default AdminResiPage;
